/**
 * PanoramaView - 360° panorama viewing mode
 */

import * as THREE from "three"
import { PanoramaControls } from "../controls/PanoramaControls.js"
import { SpatialCursor } from "../controls/SpatialCursor.js"
import { NavigationManager } from "../controls/NavigationManager.js"
import { CustomizationManager } from "../controls/CustomizationManager.js"

export class PanoramaView {
  constructor(scene, camera, renderer, model, panoramaLoader, config) {
    this.scene = scene
    this.camera = camera
    this.renderer = renderer
    this.model = model
    this.panoramaLoader = panoramaLoader
    this.config = config

    this.currentPanoId = null
    this.isActive = false
    this.currentPanoramaGroup = null
    this.transitionPanoramaGroup = null
    this.isTransitioning = false // Prevent overlapping transitions

    // Parse URL parameters for UI customization
    const urlParams = new URLSearchParams(window.location.search)
    this.showOverlay = urlParams.get("showOverlay") !== "false" // Default to true

    // Initialize controls
    this.controls = new PanoramaControls(camera, renderer.domElement)
    this.controls.setEnabled(false)

    // Add callback to update panorama position during drag (prevents black screen)
    this.controls.onCameraMove = () => {
      if (this.isActive) {
        if (this.currentPanoramaGroup && this.currentPanoramaGroup.parent) {
          this.currentPanoramaGroup.position.copy(this.camera.position)
        }
        if (
          this.transitionPanoramaGroup &&
          this.transitionPanoramaGroup.parent
        ) {
          this.transitionPanoramaGroup.position.copy(this.camera.position)
        }
      }
    }

    // Initialize spatial cursor
    this.spatialCursor = new SpatialCursor(scene, camera, model)
    this.spatialCursor.setEnabled(false)

    // Initialize navigation manager
    this.navigationManager = new NavigationManager({
      spatialCursor: this.spatialCursor,
      scene,
      camera,
      config,
      model,
      renderer,
    })

    // Initialize customization manager with tourId from config
    const tourId = config.tourConfig?.tourId || null
    this.customizationManager = new CustomizationManager(
      scene,
      panoramaLoader,
      tourId,
    )
    this.customizationSelections = new Map()
    this.customizationUI = null

    // Callbacks
    this.onNavigateCallback = null

    // Drag-safe click handling
    this.isDragging = false
    this.mouseDownPos = new THREE.Vector2()

    this.renderer.domElement.addEventListener("mousedown", (event) => {
      this.mouseDownPos.set(event.clientX, event.clientY)
      this.isDragging = false
    })

    this.renderer.domElement.addEventListener("mousemove", (event) => {
      const moveThreshold = 5 // pixels
      const dx = event.clientX - this.mouseDownPos.x
      const dy = event.clientY - this.mouseDownPos.y
      if (Math.sqrt(dx * dx + dy * dy) > moveThreshold) {
        this.isDragging = true
      }
    })

    this.renderer.domElement.addEventListener("mouseup", (event) => {
      if (!this.isActive) return
      if (this.isDragging) return // Ignore if it was a drag

      // Check if clicking on a hotspot - this takes priority
      const clickedHotspot = this.getClickedHotspot(event)
      if (clickedHotspot) {
        console.log("🎯 Clicked hotspot:", clickedHotspot.userData.targetPanoId)
        this.navigateTo(clickedHotspot.userData.targetPanoId)
        return
      }

      // Otherwise, navigate to nearest panorama in click direction
      const nearestPanoId = this.getNearestPanoramaFromClick(event)
      console.log("📍 Navigating to nearest panorama:", nearestPanoId)
      if (nearestPanoId && this.config.panoramas[nearestPanoId]) {
        this.navigateTo(nearestPanoId)
      } else if (nearestPanoId) {
        console.warn("⚠️ Panorama not found in config:", nearestPanoId)
      }
    })
  }

  /**
   * Enter panorama view
   */
  async enter(panoId, options = {}) {
    this.isActive = true
    this.currentPanoId = panoId

    const panoConfig = this.config.panoramas[panoId]
    if (!panoConfig) {
      console.error(`❌ Panorama ${panoId} not found in config`)
      return
    }

    if (this.currentPanoramaGroup) {
      this.scene.remove(this.currentPanoramaGroup)
      this.disposePanoramaGroup(this.currentPanoramaGroup)
      this.currentPanoramaGroup = null
    }

    try {
      this.currentPanoramaGroup = await this.createPanoramaGroup(
        panoId,
        panoConfig,
      )
      this.scene.add(this.currentPanoramaGroup)
      this.scene.background = new THREE.Color(0x000000)

      // Ensure panorama is fully ready with proper opacity
      const mesh = this.currentPanoramaGroup.children[0]
      if (mesh && mesh.material && mesh.material.uniforms) {
        mesh.material.uniforms.opacity.value = 1.0
        mesh.material.uniforms.brightness.value = 1.0
      }
    } catch (error) {
      console.error(`Failed to load panorama ${panoId}:`, error)
      return
    }

    const cameraX = panoConfig.position.x + this.model.position.x
    const cameraY = panoConfig.position.y + this.model.position.y
    const cameraZ = panoConfig.position.z + this.model.position.z
    this.camera.position.set(cameraX, cameraY, cameraZ)

    // CRITICAL: Position panorama group at camera position immediately
    this.currentPanoramaGroup.position.copy(this.camera.position)

    const initialRotY =
      options.initialRotY !== undefined
        ? options.initialRotY
        : panoConfig.rotation.y || 0
    const initialRotX =
      options.initialRotX !== undefined
        ? options.initialRotX
        : panoConfig.rotation.x || 0
    this.controls.setRotation(initialRotY, initialRotX)

    this.makeModelInvisible()

    // Pre-prepare model materials for smooth transitions
    // Store cloned materials for future use so first navigation is smooth
    this.prepareModelMaterialsForFutureTransitions()

    this.controls.setEnabled(true)
    this.spatialCursor.setEnabled(true)

    this.navigationManager.createHotspots(panoId)
    this.navigationManager.onNavigate((targetId) => {
      if (this.onNavigateCallback) this.onNavigateCallback(targetId)
    })

    this.updateUI(panoConfig)
    this.loadCustomizationOptionsAsync(panoId, panoConfig)
  }

  async loadCustomizationOptionsAsync(panoId, panoConfig) {
    try {
      const items = await this.customizationManager.loadCustomizationOptions(
        panoId,
      )
      if (this.customizationUI) {
        this.customizationUI.updateItems(items)
        this.customizationUI.show()
      }

      const savedSelections = this.customizationSelections.get(panoId)
      if (savedSelections) {
        await this.customizationManager.restoreSelections(
          savedSelections,
          this.camera.position,
          panoConfig.panoRotY,
        )
      }
    } catch (error) {
      console.error("Failed to load customization options:", error)
    }
  }

  exit() {
    if (this.currentPanoId) {
      this.customizationSelections.set(
        this.currentPanoId,
        this.customizationManager.getSelections(),
      )
    }

    this.isActive = false
    this.controls.setEnabled(false)
    this.spatialCursor.setEnabled(false)
    this.navigationManager.clearHotspots()

    if (this.currentPanoramaGroup) {
      this.scene.remove(this.currentPanoramaGroup)
      this.disposePanoramaGroup(this.currentPanoramaGroup)
      this.currentPanoramaGroup = null
    }
    if (this.transitionPanoramaGroup) {
      this.scene.remove(this.transitionPanoramaGroup)
      this.disposePanoramaGroup(this.transitionPanoramaGroup)
      this.transitionPanoramaGroup = null
    }

    this.scene.background = new THREE.Color(0x1a1a2e)
    this.camera.rotation.order = "XYZ"
    this.customizationManager.clearAll()
    if (this.customizationUI) this.customizationUI.hide()
    this.makeModelVisible()

    // Restore UI elements if they were hidden
    this.restoreUIElements()
  }

  restoreUIElements() {
    // Restore mode switcher (Walkthrough View / Model View buttons)
    const modeSwitcher = document.getElementById("mode-switcher")
    if (modeSwitcher) modeSwitcher.style.display = ""

    // Restore navigation info (Welcome / Click to explore)
    const navInfo = document.getElementById("nav-info")
    if (navInfo) navInfo.style.display = ""

    // Restore minimap
    const minimapContainer = document.getElementById("minimap-container")
    if (minimapContainer) minimapContainer.style.display = ""

    // Restore panorama details (top left) - for backwards compatibility
    const locationElement = document.getElementById("current-location")
    const hintElement = document.getElementById("nav-hint")
    if (locationElement) locationElement.style.display = ""
    if (hintElement) hintElement.style.display = ""

    // Restore the parent container if it exists - for backwards compatibility
    const panoInfo = document.getElementById("pano-info")
    if (panoInfo) panoInfo.style.display = ""

    // Restore walkthrough view option - for backwards compatibility
    const viewLabel = document.getElementById("view-label")
    const viewIcon = document.getElementById("view-icon")
    if (viewLabel) viewLabel.style.display = ""
    if (viewIcon) viewIcon.style.display = ""

    // Restore any parent view toggle container - for backwards compatibility
    const viewToggle = document.getElementById("view-toggle")
    if (viewToggle) viewToggle.style.display = ""

    // Restore customization UI if it exists
    if (this.customizationUI) {
      this.customizationUI.show()
    }
  }

  update() {
    if (!this.isActive) return

    // Update controls
    this.controls.update()

    // Update panorama positions
    if (this.currentPanoramaGroup && this.currentPanoramaGroup.parent) {
      this.currentPanoramaGroup.position.copy(this.camera.position)
    }
    if (this.transitionPanoramaGroup && this.transitionPanoramaGroup.parent) {
      this.transitionPanoramaGroup.position.copy(this.camera.position)
    }

    // Update cursor
    this.spatialCursor.update()
    this.navigationManager.update()
    this.customizationManager.update(this.camera.position)

    // Hotspot hover check
    const overHotspot = this.spatialCursor.isOverHotspot(
      this.navigationManager.hotspots,
    )

    // Prevent floor hotspots from reacting
    let interactive = false
    if (overHotspot) {
      const normalY = overHotspot.userData.normal?.y ?? 1 // assume up if undefined
      if (normalY < 0.7) {
        interactive = true // only non-floor hotspots trigger interaction
      }
    }

    //this.spatialCursor.setInteractiveMode(interactive);
  }

  makeModelInvisible() {
    this.model.traverse((child) => {
      if (child.isMesh) {
        if (!child.userData.originalMaterial)
          child.userData.originalMaterial = child.material
        if (!child.userData.invisibleMaterial) {
          child.userData.invisibleMaterial = new THREE.MeshBasicMaterial({
            colorWrite: false,
            depthWrite: true,
            depthTest: true,
            side: THREE.FrontSide,
          })
        }
        child.material = child.userData.invisibleMaterial
        child.visible = true
        child.renderOrder = -2
        child.userData.isGeometry = true
      }
    })
  }

  makeModelVisible() {
    this.model.traverse((child) => {
      if (child.isMesh) {
        child.visible = true
        if (child.userData.originalMaterial) {
          child.material = child.userData.originalMaterial
          child.material.visible = true
          child.material.colorWrite = true
          child.material.depthWrite = true
          child.material.depthTest = true
          child.material.needsUpdate = true
        }
        child.userData.isGeometry = false
      }
    })
    this.model.visible = true
  }

  updateUI(panoConfig) {
    // If showOverlay is false, hide all UI elements
    if (!this.showOverlay) {
      this.hideUIElements()
      return
    }

    const locationElement = document.getElementById("current-location")
    const hintElement = document.getElementById("nav-hint")
    if (locationElement)
      locationElement.textContent =
        panoConfig.name || `Panorama ${this.currentPanoId}`
    if (hintElement)
      hintElement.textContent =
        "Drag to look around • Click hotspots to navigate"
    const viewLabel = document.getElementById("view-label")
    const viewIcon = document.getElementById("view-icon")
    if (viewLabel) viewLabel.textContent = "Panorama View"
    if (viewIcon) viewIcon.textContent = "📷"
  }

  hideUIElements() {
    // Hide mode switcher (Walkthrough View / Model View buttons)
    const modeSwitcher = document.getElementById("mode-switcher")
    if (modeSwitcher) modeSwitcher.style.display = "none"

    // Hide navigation info (Welcome / Click to explore)
    const navInfo = document.getElementById("nav-info")
    if (navInfo) navInfo.style.display = "none"

    // Hide minimap
    const minimapContainer = document.getElementById("minimap-container")
    if (minimapContainer) minimapContainer.style.display = "none"

    // Hide panorama details (top left) - for backwards compatibility
    const locationElement = document.getElementById("current-location")
    const hintElement = document.getElementById("nav-hint")
    if (locationElement) locationElement.style.display = "none"
    if (hintElement) hintElement.style.display = "none"

    // Hide the parent container if it exists - for backwards compatibility
    const panoInfo = document.getElementById("pano-info")
    if (panoInfo) panoInfo.style.display = "none"

    // Hide walkthrough view option - for backwards compatibility
    const viewLabel = document.getElementById("view-label")
    const viewIcon = document.getElementById("view-icon")
    if (viewLabel) viewLabel.style.display = "none"
    if (viewIcon) viewIcon.style.display = "none"

    // Hide any parent view toggle container - for backwards compatibility
    const viewToggle = document.getElementById("view-toggle")
    if (viewToggle) viewToggle.style.display = "none"

    // Hide customization UI if it exists
    if (this.customizationUI) {
      this.customizationUI.hide()
    }
  }

  async navigateTo(panoId) {
    if (panoId === this.currentPanoId) return

    // Prevent overlapping transitions
    if (this.isTransitioning) {
      console.log("⚠️ Transition already in progress, ignoring click")
      return
    }

    this.isTransitioning = true

    this.spatialCursor.setEnabled(false)
    this.renderer.domElement.dataset.state = "moving"
    this.navigationManager.hideTooltip()

    try {
      // Save current customizations
      if (this.currentPanoId) {
        this.customizationSelections.set(
          this.currentPanoId,
          this.customizationManager.getSelections(),
        )
      }

      // Ensure current panorama is fully opaque before transition
      if (this.currentPanoramaGroup && this.currentPanoramaGroup.children[0]) {
        const mesh = this.currentPanoramaGroup.children[0]
        if (mesh.material && mesh.material.uniforms) {
          mesh.material.uniforms.opacity.value = 1.0
          mesh.material.uniforms.brightness.value = 1.0
        }
      }

      await this.travelTransition(this.currentPanoId, panoId)
      this.currentPanoId = panoId
      this.navigationManager.clearHotspots()
      this.navigationManager.createHotspots(panoId)
      const panoConfig = this.config.panoramas[panoId]
      if (panoConfig) this.updateUI(panoConfig)
      this.loadCustomizationOptionsAsync(panoId, panoConfig)
    } catch (error) {
      console.error("❌ Navigation error:", error)
    } finally {
      this.isTransitioning = false
      this.renderer.domElement.dataset.state = "grab"
    }
  }

  async travelTransition(fromPanoId, toPanoId) {
    const duration = 1200 // Smooth transition duration
    const startTime = performance.now()
    const fromConfig = this.config.panoramas[fromPanoId]
    const toConfig = this.config.panoramas[toPanoId]

    // Validate panorama configs exist
    if (!toConfig) {
      console.error(`❌ Target panorama ${toPanoId} not found in config`)
      return
    }

    const startRotY = this.controls.rotY
    const startRotX = this.controls.rotX
    const startFOV = this.camera.fov

    const startPos = new THREE.Vector3(
      this.camera.position.x,
      this.camera.position.y,
      this.camera.position.z,
    )

    const targetPos = new THREE.Vector3(
      toConfig.position.x + this.model.position.x,
      toConfig.position.y + this.model.position.y,
      toConfig.position.z + this.model.position.z,
    )

    const travelDistance = startPos.distanceTo(targetPos)

    // CRITICAL: Clone the current panorama for fade-out
    // This ensures it stays visible and fades out independently
    let fadingPanoramaGroup = null
    let fadingMesh = null

    if (this.currentPanoramaGroup) {
      // Keep reference to current group for fade-out
      fadingPanoramaGroup = this.currentPanoramaGroup
      fadingMesh = fadingPanoramaGroup.children[0]

      // Ensure it's at full opacity before fade-out starts
      if (fadingMesh && fadingMesh.material && fadingMesh.material.uniforms) {
        fadingMesh.material.uniforms.opacity.value = 1.0
        fadingMesh.material.uniforms.brightness.value = 1.0
      }

      // Set current to null so we don't interfere with it during transition
      this.currentPanoramaGroup = null
    }

    // Create target panorama and add to scene immediately (at 0 opacity)
    // This ensures all GPU work (geometry, shaders) is done before fade-in
    this.transitionPanoramaGroup = await this.createPanoramaGroup(
      toPanoId,
      toConfig,
    )
    this.transitionPanoramaGroup.position.copy(this.camera.position)

    // Add to scene immediately but invisible
    this.scene.add(this.transitionPanoramaGroup)
    const transitionMesh = this.transitionPanoramaGroup.children[0]
    if (
      transitionMesh &&
      transitionMesh.material &&
      transitionMesh.material.uniforms
    ) {
      transitionMesh.material.uniforms.opacity.value = 0
      transitionMesh.material.uniforms.brightness.value = 1.0
    }

    // Pre-configure model materials for smooth transitions (prevents first-visit blink)
    // This prepares materials at 0 opacity WITHOUT blocking the view
    this.prepareModelForTransition()

    // Make model visible in scene for rendering (at 0 opacity, won't show yet)
    this.model.visible = true

    this.navigationManager.clearHotspots()

    this.customizationManager.hideAllOverlays()

    // Wait one frame to ensure materials are ready before starting animation
    // This allows the panorama to stay visible while model prepares
    await new Promise((resolve) => requestAnimationFrame(resolve))

    return new Promise((resolve) => {
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Smooth ease-in-out curve (cubic for natural motion)
        const t =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2

        // Straight line travel with smooth motion
        this.camera.position.x = startPos.x + (targetPos.x - startPos.x) * t
        this.camera.position.y = startPos.y + (targetPos.y - startPos.y) * t
        this.camera.position.z = startPos.z + (targetPos.z - startPos.z) * t

        // Maintain viewing direction - no head bob
        this.controls.rotY = startRotY
        this.controls.rotX = startRotX
        this.controls.targetRotY = startRotY
        this.controls.targetRotX = startRotX

        // Keep FOV constant - no breathing effect
        this.camera.fov = startFOV
        this.camera.updateProjectionMatrix()

        // Enhanced panorama transitions - smooth crossfades
        let fromPanoOpacity, toPanoOpacity, modelOpacity

        if (progress < 0.5) {
          // Phase 1: Fade out current panorama while fading in model (0-50%)
          const fadeProgress = progress / 0.5
          // Panorama fades out gradually over first half
          fromPanoOpacity = 1 - fadeProgress
          toPanoOpacity = 0
          // Model fades in simultaneously for smooth crossfade
          modelOpacity = fadeProgress
        } else if (progress < 0.75) {
          // Phase 2: Model fully visible during travel (50-75%)
          fromPanoOpacity = 0
          toPanoOpacity = 0
          modelOpacity = 1.0
        } else if (progress < 0.96) {
          // Phase 3: Crossfade from model to target panorama (75-96%)
          const fadeProgress = (progress - 0.75) / 0.21
          fromPanoOpacity = 0
          // Target panorama fades in
          toPanoOpacity = fadeProgress
          // Model fades out simultaneously for smooth crossfade
          modelOpacity = 1.0 - fadeProgress
        } else {
          // Phase 4: Final frames - target fully visible (96-100%)
          fromPanoOpacity = 0
          toPanoOpacity = 1.0
          modelOpacity = 0
        }

        // Apply fading panorama opacity (fade out during first 50%)
        if (fadingMesh && fadingMesh.material && fadingMesh.material.uniforms) {
          fadingMesh.material.uniforms.opacity.value = Math.max(
            0,
            Math.min(1, fromPanoOpacity),
          )
        }

        this.makeModelVisibleWithOpacity(Math.max(0, Math.min(1, modelOpacity)))

        // IMPORTANT: Keep fading panorama in scene during entire fade-out period (0-50%)
        // Only remove it AFTER fade-out is complete at 25%
        if (
          progress >= 0.4 &&
          fadingPanoramaGroup &&
          fadingPanoramaGroup.parent
        ) {
          this.scene.remove(fadingPanoramaGroup)
          this.disposePanoramaGroup(fadingPanoramaGroup)
          fadingPanoramaGroup = null
          fadingMesh = null
        }

        // Update target panorama opacity (it's already in the scene from the start)
        if (
          transitionMesh &&
          transitionMesh.material &&
          transitionMesh.material.uniforms
        ) {
          transitionMesh.material.uniforms.opacity.value = Math.max(
            0,
            Math.min(1, toPanoOpacity),
          )
        }

        // Subtle background brightness based on model visibility
        const bgBrightness = modelOpacity * 0.08
        this.scene.background = new THREE.Color(
          bgBrightness,
          bgBrightness,
          bgBrightness,
        )

        // Update panorama group positions for perfect sync with camera movement
        if (fadingPanoramaGroup && fadingPanoramaGroup.parent) {
          fadingPanoramaGroup.position.copy(this.camera.position)
        }
        if (
          this.transitionPanoramaGroup &&
          this.transitionPanoramaGroup.parent
        ) {
          this.transitionPanoramaGroup.position.copy(this.camera.position)
        }

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          // Clean up any remaining fading panorama
          if (fadingPanoramaGroup && fadingPanoramaGroup.parent) {
            this.scene.remove(fadingPanoramaGroup)
            this.disposePanoramaGroup(fadingPanoramaGroup)
          }

          this.cleanupTransition(resolve)
        }
      }
      requestAnimationFrame(animate)
    })
  }

  /**
   * Pre-prepare cloned materials during panorama entry
   * This ensures smooth transitions even on first navigation
   */
  prepareModelMaterialsForFutureTransitions() {
    this.model.traverse((child) => {
      if (child.isMesh && child.userData.originalMaterial) {
        // Clone and store transparent material for future use
        if (!child.userData.transparentMaterial) {
          child.userData.transparentMaterial =
            child.userData.originalMaterial.clone()
          child.userData.transparentMaterial.transparent = true
          child.userData.transparentMaterial.opacity = 1.0
          child.userData.transparentMaterial.depthWrite = true
          child.userData.transparentMaterial.needsUpdate = true
        }
      }
    })
  }

  /**
   * Pre-configure model materials for smooth transitions
   * Sets model to visible materials at 0 opacity for smooth fade-in
   */
  prepareModelForTransition() {
    this.model.traverse((child) => {
      if (child.isMesh && child.userData.originalMaterial) {
        // Use pre-cloned material if available, otherwise clone now
        if (child.userData.transparentMaterial) {
          child.material = child.userData.transparentMaterial
          child.material.opacity = 0 // Start at 0 for fade-in
          child.material.depthWrite = false
          child.material.visible = true
        } else {
          // Fallback: clone if not pre-prepared (first time)
          const clonedMaterial = child.userData.originalMaterial.clone()
          clonedMaterial.transparent = true
          clonedMaterial.opacity = 0 // Start at 0 for fade-in
          clonedMaterial.depthWrite = false
          clonedMaterial.visible = true
          clonedMaterial.needsUpdate = true

          // Apply the material
          child.material = clonedMaterial

          // Store for future use
          child.userData.transparentMaterial = clonedMaterial
        }
        // Ensure mesh is visible but material is transparent
        child.visible = true
      }
    })
  }

  makeModelVisibleWithOpacity(opacity) {
    this.model.traverse((child) => {
      if (child.isMesh && child.userData.originalMaterial) {
        // Material is already prepared, just update opacity
        child.material.transparent = true
        child.material.opacity = opacity
        child.material.depthWrite = opacity > 0.5
        child.visible = opacity > 0

        // Add emissive brightness to compensate for transparency darkness
        // This makes the model appear brighter during transition
        if (opacity > 0 && opacity < 1) {
          // Store original emissive if not already stored
          if (child.userData.originalEmissive === undefined) {
            child.userData.originalEmissive = child.material.emissive
              ? child.material.emissive.clone()
              : new THREE.Color(0x000000)
            child.userData.originalEmissiveIntensity =
              child.material.emissiveIntensity || 0
          }

          // Add emissive boost during transparency (up to 50% of the base color)
          // This compensates for the darkness caused by transparency
          if (child.material.color) {
            const boostIntensity = (1 - opacity) * 0.5 // Stronger boost when more transparent
            child.material.emissive = child.material.color
              .clone()
              .multiplyScalar(boostIntensity)
            child.material.emissiveIntensity = 1.0
          }
        } else {
          // Restore original emissive when fully opaque or invisible
          if (child.userData.originalEmissive !== undefined) {
            child.material.emissive = child.userData.originalEmissive.clone()
            child.material.emissiveIntensity =
              child.userData.originalEmissiveIntensity
          }
        }
      }
    })
  }

  cleanupTransition(resolve) {
    // Clean up old panorama
    if (this.currentPanoramaGroup) {
      this.scene.remove(this.currentPanoramaGroup)
      this.disposePanoramaGroup(this.currentPanoramaGroup)
    }

    // Set the transition panorama as current
    this.currentPanoramaGroup = this.transitionPanoramaGroup
    this.transitionPanoramaGroup = null

    // Ensure the new panorama is properly set up
    if (this.currentPanoramaGroup) {
      // Make sure it's in the scene
      if (!this.currentPanoramaGroup.parent) {
        this.scene.add(this.currentPanoramaGroup)
      }

      // Position it correctly
      this.currentPanoramaGroup.position.copy(this.camera.position)

      // Ensure full opacity
      const mesh = this.currentPanoramaGroup.children[0]
      if (mesh && mesh.material && mesh.material.uniforms) {
        mesh.material.uniforms.opacity.value = 1.0
        mesh.material.uniforms.brightness.value = 1.0
      }
    }

    // Reset camera and scene
    this.camera.fov = 75
    this.camera.updateProjectionMatrix()
    this.scene.background = new THREE.Color(0x000000)
    //this.scene.background = new THREE.Color(0x101010);

    // Hide model
    this.makeModelInvisible()

    if (resolve) resolve()
  }

  /**
   * Check if a hotspot was clicked directly (raycast against hotspots)
   */
  getClickedHotspot(event) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    )

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, this.camera)

    // Raycast against hotspot meshes
    const intersects = raycaster.intersectObjects(
      this.navigationManager.hotspots,
      true,
    )

    if (intersects.length > 0) {
      // Find the hotspot group from the intersected object
      let object = intersects[0].object
      while (object && !object.userData.type) {
        object = object.parent
      }

      if (object && object.userData.type === "navigationHotspot") {
        return object
      }
    }

    return null
  }

  /**
   * Check if there's a panorama ahead in the camera's forward direction
   */
  hasPanoramaAhead() {
    const cameraDir = new THREE.Vector3(0, 0, -1)
    cameraDir.applyQuaternion(this.camera.quaternion).normalize()

    const forwardCone = Math.PI / 3 // 60° cone in front

    for (const [panoId, panoConfig] of Object.entries(this.config.panoramas)) {
      if (panoId === this.currentPanoId) continue

      const panoPos = new THREE.Vector3(
        panoConfig.position.x + this.model.position.x,
        panoConfig.position.y + this.model.position.y,
        panoConfig.position.z + this.model.position.z,
      )

      const dirToPano = panoPos.clone().sub(this.camera.position).normalize()
      const angle = cameraDir.angleTo(dirToPano)

      // If panorama is within forward cone, we have a path ahead
      if (angle < forwardCone) {
        return true
      }
    }

    return false
  }

  /**
   * Get nearest panorama in click direction
   * Finds panorama closest to the line between camera view direction and click direction
   */
  getNearestPanoramaFromClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    )

    // Check if there's a panorama ahead (not a dead end)
    const hasPathAhead = this.hasPanoramaAhead()

    // If dead end, implement left/center/right zone logic
    if (!hasPathAhead) {
      // Normalize mouse.x to 0-1 range (0 = left edge, 1 = right edge)
      const normalizedX = (mouse.x + 1) / 2

      // Define zones: 35% left, 30% center (blocked), 35% right
      const leftZoneEnd = 0.35
      const centerZoneEnd = 0.65

      console.log("Dead end detected - using zone navigation")
      console.log(`Click position: ${(normalizedX * 100).toFixed(1)}%`)

      // Center zone (30%) - block navigation
      if (normalizedX >= leftZoneEnd && normalizedX <= centerZoneEnd) {
        console.log("Center zone - no navigation")
        return null
      }

      // Get camera right direction (for determining left vs right)
      const cameraRight = new THREE.Vector3(1, 0, 0)
      cameraRight.applyQuaternion(this.camera.quaternion).normalize()

      const cameraLeft = cameraRight.clone().negate()

      // Determine target direction based on zone
      let targetDirection
      if (normalizedX < leftZoneEnd) {
        console.log("Left zone - finding nearest left panorama")
        targetDirection = cameraLeft
      } else {
        console.log("Right zone - finding nearest right panorama")
        targetDirection = cameraRight
      }

      // Find nearest panorama in the target direction
      return this.findNearestPanoramaInDirection(targetDirection)
    }

    // Normal behavior when there's a path ahead
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, this.camera)
    const clickDir = raycaster.ray.direction.clone().normalize()

    // Get camera viewing direction
    const cameraDir = new THREE.Vector3(0, 0, -1)
    cameraDir.applyQuaternion(this.camera.quaternion).normalize()

    let nearestPanoId = null
    let bestScore = Infinity

    // Group panoramas by distance from current position (rings)
    const panoramasByDistance = []
    for (const [panoId, panoConfig] of Object.entries(this.config.panoramas)) {
      if (panoId === this.currentPanoId) continue

      const panoPos = new THREE.Vector3(
        panoConfig.position.x + this.model.position.x,
        panoConfig.position.y + this.model.position.y,
        panoConfig.position.z + this.model.position.z,
      )

      const distance = this.camera.position.distanceTo(panoPos)
      panoramasByDistance.push({ panoId, panoConfig, panoPos, distance })
    }

    // Sort by distance to identify rings
    panoramasByDistance.sort((a, b) => a.distance - b.distance)

    // Find the best panorama considering:
    // 1. Angle to click direction (primary)
    // 2. Distance from camera (secondary - prefer closer rings)
    for (const pano of panoramasByDistance) {
      const dirToPano = pano.panoPos
        .clone()
        .sub(this.camera.position)
        .normalize()

      // Calculate angle between click direction and direction to panorama
      const angleToClick = clickDir.angleTo(dirToPano)

      // Calculate angle between camera direction and direction to panorama
      const angleToCamera = cameraDir.angleTo(dirToPano)

      // Score based on:
      // - Primary: How aligned the panorama is with click direction (lower angle = better)
      // - Secondary: Distance (normalized, prefer closer)
      // - Tertiary: How aligned with camera view (prefer forward movement)
      const angleWeight = 1000 // Angle is most important
      const distanceWeight = 1 // Distance is secondary
      const cameraAlignWeight = 0.5 // Slight preference for forward-facing panos

      const score =
        angleToClick * angleWeight +
        pano.distance * distanceWeight +
        angleToCamera * cameraAlignWeight

      // Only consider panoramas within a reasonable cone (90° from click)
      if (angleToClick < Math.PI / 2 && score < bestScore) {
        bestScore = score
        nearestPanoId = pano.panoId
      }
    }

    // Fallback: if no panorama in front of click, pick the absolute closest one
    if (!nearestPanoId && panoramasByDistance.length > 0) {
      nearestPanoId = panoramasByDistance[0].panoId
    }

    return nearestPanoId
  }

  /**
   * Find nearest panorama in a specific direction
   */
  findNearestPanoramaInDirection(direction) {
    let nearestPanoId = null
    let bestScore = Infinity

    for (const [panoId, panoConfig] of Object.entries(this.config.panoramas)) {
      if (panoId === this.currentPanoId) continue

      const panoPos = new THREE.Vector3(
        panoConfig.position.x + this.model.position.x,
        panoConfig.position.y + this.model.position.y,
        panoConfig.position.z + this.model.position.z,
      )

      const dirToPano = panoPos.clone().sub(this.camera.position).normalize()
      const distance = this.camera.position.distanceTo(panoPos)

      // Calculate angle between target direction and direction to panorama
      const angle = direction.angleTo(dirToPano)

      // Score based on angle and distance (prefer aligned and closer panoramas)
      const angleWeight = 100
      const distanceWeight = 1
      const score = angle * angleWeight + distance * distanceWeight

      // Only consider panoramas within 90° of target direction
      if (angle < Math.PI / 2 && score < bestScore) {
        bestScore = score
        nearestPanoId = panoId
      }
    }

    return nearestPanoId
  }

  async createPanoramaGroup(panoId, panoConfig) {
    const texture = await this.panoramaLoader.load(panoId)

    const group = new THREE.Group()
    const geometry = new THREE.BoxGeometry(500, 500, 500)
    const material = new THREE.ShaderMaterial({
      uniforms: {
        tCube: { value: texture },
        opacity: { value: 1.0 },
        brightness: { value: 1.0 },
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform samplerCube tCube;
        uniform float opacity;
        uniform float brightness;
        varying vec3 vPosition;

        vec3 linearToSRGB(vec3 value) {
          return pow(value, vec3(1.0 / 2.2));
        }

        void main() {
          vec3 direction = normalize(vPosition);
          direction.z = -direction.z;  // Flip Z for correct mapping

          // Sample cubemap (in linear space)
          vec4 texColor = textureCube(tCube, direction);

          // Convert to sRGB for correct brightness
          vec3 corrected = linearToSRGB(texColor.rgb);

          // Apply brightness multiplier
          corrected *= brightness;

          gl_FragColor = vec4(corrected, texColor.a * opacity);
        }
      `,
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.renderOrder = -1
    group.add(mesh)

    const panoRotY = panoConfig.panoRotY || 0
    group.rotation.y = THREE.MathUtils.degToRad(panoRotY)

    return group
  }

  disposePanoramaGroup(group) {
    if (!group) return
    group.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) child.material.dispose()
    })
  }

  onNavigate(callback) {
    this.onNavigateCallback = callback
  }

  getCurrentViewingDirection() {
    return { rotY: this.controls.rotY, rotX: this.controls.rotX }
  }

  /**
   * Preload all panoramas in the background to eliminate first-visit glitches
   */
  async preloadAllPanoramas() {
    const panoIds = Object.keys(this.config.panoramas)
    if (panoIds.length === 0) {
      console.log("⚠️ No panoramas to preload")
      return
    }

    console.log(`🔄 Preloading ${panoIds.length} panoramas in background...`)

    try {
      await this.panoramaLoader.preload(panoIds)
      console.log(`✅ All panoramas preloaded successfully!`)
    } catch (error) {
      console.error("❌ Error preloading panoramas:", error)
    }
  }

  dispose() {
    this.controls.dispose()
    this.spatialCursor.dispose()
    this.navigationManager.dispose()
  }
}
